import { useAppTranslation } from "@/utils";
import { Button, Card, Flex, Radio, Typography } from "@payping/web-components";
import { FC, useEffect, useState } from "react";
import { GuaranteeType } from "@payping/api/app/api/bnplMerchantTypes";
import NoWarrantyIcon from "./Icons/NoWarranty";
import SalaryDeductionIcon from "./Icons/SalaryDeduction";
import PromissoryIcon from "./Icons/PromissoryIcon";
import { PlanData } from ".";
// import ChequeIcon from "./Icons/Cheque";

enum ContractType {
  withContract = "1",
  withoutContract = "2",
}
interface GuaranteeTypes {
  title: string;
  description: string;
  icon: any;
  value: number;
  active?: boolean;
}
interface ContractResponse {
  contractIsMandatory: boolean;
  guaranteeType: GuaranteeType | undefined;
}

type Props = {
  onNext: (value: ContractResponse) => void;
  onPrev: () => void;
  loading: boolean;
  planData: PlanData;
  selectedGuarantyItem: GuaranteeType | undefined;
  setSelectedGuarantyItem: (value: GuaranteeType) => void;
};

const PlanContract: FC<Props> = ({
  onNext,
  onPrev,
  loading,
  planData,
  setSelectedGuarantyItem,
  selectedGuarantyItem,
}) => {
  const { t } = useAppTranslation();
  const [contractType, setContractType] = useState<string>(ContractType.withContract);
  const [guaranteeItems, setGuaranteeItems] = useState<Array<GuaranteeTypes>>([
    {
      icon: NoWarrantyIcon,
      value: GuaranteeType.None,
      title: t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.guaranteeItemTitle"),
      description: t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.guaranteeItemDescription"),
      active: true,
    },
    // {
    //   icon: ChequeIcon,
    //   value: GuaranteeType.Cheque,
    //   title: t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.chequeTitle"),
    //   description: t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.chequeDescription"),
    //   active: false,
    // },
    {
      icon: PromissoryIcon,
      value: GuaranteeType.Promissory,
      title: t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.promissoryTitle"),
      description: t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.promissoryDescription"),
      active: false,
    },
    {
      icon: SalaryDeductionIcon,
      value: GuaranteeType.Salary,
      title: t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.salaryTitle"),
      description: t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.salaryDescription"),
      active: false,
    },
  ]);

  useEffect(() => {
    if (
      planData.contractIsMandatory === true ||
      planData.contractIsMandatory === null ||
      planData.contractIsMandatory === undefined
    ) {
      setContractType(ContractType.withContract);
    } else {
      setContractType(ContractType.withoutContract);
    }
    if (planData.guaranteeType) {
      setSelectedGuarantyItem(planData.guaranteeType);

      setGuaranteeItems((prevGuaranteeItems) => {
        const index = prevGuaranteeItems.findIndex((a) => a.value === planData.guaranteeType);

        const currentActiveIndex = prevGuaranteeItems.findIndex((a) => a.active);

        if (index !== -1 && index !== currentActiveIndex) {
          return prevGuaranteeItems.map((g, i) => ({
            ...g,
            active: i === index,
          }));
        }

        return prevGuaranteeItems;
      });
    }
  }, [planData, setGuaranteeItems]);

  const options = [
    { label: t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.withContract"), value: ContractType.withContract },
    {
      label: t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.withoutContract"),
      value: ContractType.withoutContract,
    },
  ];

  const onChangeGuaranty = (item: GuaranteeTypes, index: number) => {
    const updatedGuaranteeItems = guaranteeItems.map((g) => ({
      ...g,
      active: false,
    }));
    updatedGuaranteeItems[index].active = true;
    setGuaranteeItems([...updatedGuaranteeItems]);
    setSelectedGuarantyItem(item.value);
  };

  const allowSubmit = () => {
    const guaranteeActiveItems = guaranteeItems.filter((a) => a.active).length;
    if (guaranteeActiveItems > 0) {
      return true;
    }
    return false;
  };

  const next = () => {
    const data: ContractResponse = {
      contractIsMandatory: contractType === ContractType.withContract,
      guaranteeType: selectedGuarantyItem,
    };
    onNext(data);
  };

  return (
    <Flex vertical gap={"large"} className="tw-w-full tw-max-w-[640px] max-lg:tw-max-w-[100%]">
      <Flex vertical>
        <Typography.Title level={5} className="tw-mt-1">
          {t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.title")}
        </Typography.Title>
        <Typography.Text className="tw-mt-0">
          {t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.description")}
        </Typography.Text>

        <Flex className="tw-mt-4">
          <Radio.Group
            onChange={(e) => setContractType(e.target.value as ContractType)}
            className="tw-w-full"
            block
            options={options}
            value={contractType}
            defaultValue={ContractType.withContract}
            optionType="button"
          />
        </Flex>
        <Typography.Title level={5} className="tw-mt-5">
          {t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.setGuarantee")}
        </Typography.Title>
        <Typography.Text className="!tw-m-0">
          {t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.guaranteeDescription")}
        </Typography.Text>

        <Flex vertical gap={"small"} className="tw-mt-2">
          {guaranteeItems.map((item, index) => {
            const IconComponent = item.icon;

            return (
              <Card.Ant
                key={index}
                onClick={() => onChangeGuaranty(item, index)}
                className={`tw-mt-2 tw-w-full tw-cursor-pointer ${
                  item.active ? "tw-border-solid tw-border-color-primary" : ""
                }`}
              >
                <Flex gap="middle">
                  <IconComponent width={40} height={40} />
                  <Flex vertical justify="center">
                    <Typography.Title level={5} className="!tw-m-0">
                      {item.title}
                    </Typography.Title>
                    <Typography.Text className="tw-mt-2">{item.description}</Typography.Text>
                  </Flex>
                </Flex>
              </Card.Ant>
            );
          })}
        </Flex>
      </Flex>
      <Flex gap={"small"}>
        <Button block={false} onClick={onPrev} className="!tw-px-8" loading={false} type="default">
          {t("common.previousStep")}
        </Button>
        <Button
          block={false}
          disabled={!allowSubmit()}
          onClick={next}
          className="!tw-px-8"
          loading={loading}
          type="primary"
        >
          {selectedGuarantyItem === GuaranteeType.Salary
            ? t("common.saveAndContinue")
            : t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.saveAndCreate")}
        </Button>
      </Flex>
    </Flex>
  );
};

export default PlanContract;
